// src/pages/sales/MeetingLog.jsx
// SRS Section 3.2.2 - Meeting Log page
// Sales Reps log meetings; CEO gets notified for meetings requiring participation.

import React, { useState, useEffect } from 'react';
import {
  Calendar, Plus, Loader2, X,
  Video, Users, MapPin, FileText, AlertCircle
} from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const PLATFORMS = ['Zoom', 'Google Meet', 'Microsoft Teams', 'In-person', 'Phone'];
const OUTCOMES = ['Positive', 'Neutral', 'Negative', 'Follow-up Required', 'Deal Closed'];

// ── Log Meeting Modal ─────────────────────────────────────────────────────
const LogMeetingModal = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ceoRequired, setCeoRequired] = useState(false);

  useEffect(() => {
    const loadLeads = async () => {
      try {
        const res = await api.get('/api/sales/leads');
        const data = res.data || res;
        setLeads(Array.isArray(data) ? data : (data?.leads || data?.data || []));
      } catch (err) {
        // Silent fail — leads dropdown is optional
      }
    };
    loadLeads();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const fd = new FormData(e.target);
    const attendeesInput = fd.get('attendees') || '';
    const payload = {
      lead_id: fd.get('lead_id') || undefined,
      meeting_date: fd.get('date'),
      meeting_time: fd.get('time'),
      platform: fd.get('platform'),
      attendees: attendeesInput
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      outcome_notes: fd.get('outcome_notes'),
      requires_ceo: ceoRequired,
    };
    try {
      await api.post('/api/sales/meetings', payload);
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900 sticky top-0 bg-white z-10">
          Log Meeting
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Linked Lead (Optional)</label>
            <select name="lead_id" className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
              <option value="">No specific lead</option>
              {leads.map(l => (
                <option key={l.lead_id} value={l.lead_id}>
                  {l.client_name} — {l.service}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Date *</label>
              <input type="date" name="date" required className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Time *</label>
              <input type="time" name="time" required className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Platform *</label>
            <select name="platform" required className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Attendees *</label>
            <input name="attendees" required placeholder="Comma-separated: John, Sarah"
              className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Outcome Notes *</label>
            <textarea name="outcome_notes" required rows={3}
              placeholder="What was discussed? Next steps?"
              className="w-full p-3 border rounded-xl text-sm bg-gray-50" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Outcome</label>
            <select name="outcome" className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
              {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* CEO required toggle — SRS 3.2.2: CEO notified when meeting requires participation */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3">
            <input
              type="checkbox"
              id="ceo_required"
              checked={ceoRequired}
              onChange={(e) => setCeoRequired(e.target.checked)}
              className="w-4 h-4 accent-amber-500"
            />
            <label htmlFor="ceo_required" className="text-sm font-medium text-amber-700 cursor-pointer">
              CEO participation required — CEO will be notified
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Log Meeting
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main MeetingLog Component ─────────────────────────────────────────────
const MeetingLog = () => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  const canLog = user && ['super_admin', 'sales_manager', 'sales_rep'].includes(user.role);

  const loadMeetings = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/sales/meetings');
      const data = res.data || res;
      setMeetings(Array.isArray(data) ? data : (data?.meetings || data?.data || []));
    } catch (err) {
      setError(err.message || 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMeetings(); }, []);

  const getPlatformIcon = (platform) => {
    if (platform === 'In-person') return <MapPin className="w-4 h-4" />;
    return <Video className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Meeting Log</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track all client and prospect meetings</p>
        </div>
        {canLog && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Log Meeting
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary opacity-20" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="bg-white rounded-card border border-dashed border-gray-200 p-12 text-center">
          <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">No meetings logged yet.</p>
          {canLog && (
            <button onClick={() => setShowModal(true)}
              className="mt-3 text-primary text-sm font-bold hover:underline">
              Log your first meeting
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => (
            <div key={m.id || m.meeting_id}
              className="bg-white rounded-card border border-gray-100 shadow-soft p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className={'p-2.5 rounded-xl ' + (m.ceo_required ? 'bg-amber-50 text-amber-600' : 'bg-primary/5 text-primary')}>
                    {getPlatformIcon(m.platform)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-sm">
                        {m.lead_client_name || 'General Meeting'}
                      </h3>
                      {m.ceo_required && (
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
                          CEO Required
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                      <span>{m.date} {m.time && ('at ' + m.time)}</span>
                      <span>{m.platform}</span>
                      {m.attendees && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {m.attendees}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {m.outcome && (
                  <span className="text-xs font-bold text-gray-500 bg-gray-50 border border-gray-100 px-2.5 py-1 rounded-lg">
                    {m.outcome}
                  </span>
                )}
              </div>
              {m.outcome_notes && (
                <div className="mt-3 pt-3 border-t border-gray-50">
                  <p className="text-xs text-gray-500 leading-relaxed">{m.outcome_notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <LogMeetingModal
          onClose={() => setShowModal(false)}
          onDone={() => { setShowModal(false); loadMeetings(); }}
        />
      )}
    </div>
  );
};

export default MeetingLog;